import { FormControl, Select, MenuItem, Button, Grid, Badge } from '@mui/material';
import {
  ColumnsPanelTrigger,
  FilterPanelTrigger,
  Toolbar,
  useGridApiContext,
  useGridSelector,
  gridFilterModelSelector,
  GridViewColumnIcon,
  GridFilterListIcon,
} from '@mui/x-data-grid';
import { EDataTableType, EFeatureStatus, Status } from '../../data/Constants';
import { AppDispatch, AppState } from '../../store/AppStore';
import { setProductStatus } from '../../store/states/procos';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RecordLengthPicker } from '../RecordLengthPicker';
import { handleResetFeatureMA } from '../../components/Helpers/DataTableHelper';

import type { IProCosDataTableProps } from '../../../types';

/**
 * This component returns the toolbar for the procos data table
 * @param {IProCosDataTableProps} props input params
 * @returns {React.JSX.Element} returns react element
 */
export const ProcosCustomToolBar = (props: IProCosDataTableProps): React.JSX.Element => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const apiRef = useGridApiContext();

  const { isEditMode, productStatus, featureStatus } = useSelector(
    (state: AppState) => state.procos
  );

  // Get active filter count reactively - component will re-render when filters change
  const filterModel = useGridSelector(apiRef, gridFilterModelSelector);
  const filterCount =
    filterModel?.items?.filter(
      (item) => item.value !== undefined && item.value !== null && item.value !== ''
    ).length || 0;

  const handleSelectStatus = (e: React.ChangeEvent<HTMLSelectElement>) => {
    dispatch(setProductStatus(e.target.value));
  };

  return (
    <Toolbar
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Grid container spacing={2}>
        <Grid className="d-flex align-center justify-start" size={15}>
          <ColumnsPanelTrigger>
            <Button size="small" startIcon={<GridViewColumnIcon fontSize="small" />}>
              {t('labels.columns')}
            </Button>
          </ColumnsPanelTrigger>
          <FilterPanelTrigger>
            <Button
              size="small"
              startIcon={
                <Badge variant="dot" color="primary" invisible={filterCount === 0}>
                  <GridFilterListIcon />
                </Badge>
              }
            >
              {t('labels.filters')}
            </Button>
          </FilterPanelTrigger>
          <RecordLengthPicker
            limit={props.limit}
            handleRecordsChange={(e) => props.handleRecordsChange(e)}
          />
          {props.tableType === EDataTableType.Features && (
            <Button
              className="common-btn"
              disabled={!isEditMode}
              color="primary"
              variant="text"
              startIcon={<RestartAltIcon />}
              onClick={() => {
                handleResetFeatureMA(null, null, dispatch);
              }}
            >
              {t('button.resetAll')}
            </Button>
          )}

          {props.tableType === EDataTableType.Features && (
            <Button
              className="common-btn"
              color="primary"
              variant="contained"
              disabled={
                !isEditMode ||
                (props.tableType === EDataTableType.Features &&
                  productStatus !== EFeatureStatus.Active) ||
                !featureStatus
              }
              onClick={() => props.handleClickActive()}
            >
              {t('button.statusButton')}
            </Button>
          )}

          {props.tableType !== EDataTableType.Features && (
            <>
              &emsp; {t('labels.status') + ':'}&emsp;
              <FormControl disabled={!isEditMode} size="small" className="active-menu">
                <Select
                  disabled={
                    !isEditMode ||
                    (props.tableType === EDataTableType.Features &&
                      productStatus !== EFeatureStatus.Active)
                  }
                  id="active-select"
                  name="active-select"
                  value={productStatus}
                  onChange={(e) => handleSelectStatus(e)}
                >
                  {Object.entries(Status).map(([key, value]) => {
                    const label = t(`labels.${value}`);
                    return (
                      <MenuItem key={key} value={key}>
                        {label}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </>
          )}
        </Grid>
      </Grid>
    </Toolbar>
  );
};
